import type { PageTourDefinition, PageTourStep } from '../../components/tour/pageTourTypes';

const periodo = (extra = ''): PageTourStep => ({
  targetId: 'informe-periodo',
  title: 'Período rápido',
  body: `Elegí un rango de fechas con un toque (hoy, semana, mes, últimos meses…).${extra}`,
  placement: 'bottom',
  pad: 6,
});

/** `/ventas/informes/seguimiento-ventas` */
const SEGUIMIENTO_VENTAS_STEPS: PageTourStep[] = [
  {
    targetId: 'informe-acciones',
    title: 'Buscar y exportar',
    body: 'Con «Buscar» ejecutás el informe con los filtros elegidos y con «Limpiar» los reiniciás. Exportá los resultados a Excel o imprimilos en PDF.',
    placement: 'bottom',
    pad: 6,
  },
  periodo(),
  {
    targetId: 'informe-filtros',
    title: 'Filtros avanzados',
    body: 'Desplegá para filtrar por fechas, cliente, producto y ver solo pendientes o solo artículos inventariados.',
    placement: 'bottom',
    pad: 4,
  },
  {
    targetId: 'informe-kpis',
    title: 'Indicadores',
    body: 'Cantidad de resultados y totales pendientes de facturar y de entregar según los filtros aplicados.',
    placement: 'bottom',
    pad: 4,
  },
  {
    targetId: 'informe-resultados',
    title: 'Resultados',
    body: 'Cada renglón muestra lo pedido, facturado, entregado y devuelto. Buscá dentro de los resultados y tocá una fila para ver su hilo documental.',
    placement: 'top',
    pad: 4,
  },
  {
    targetId: 'informe-fila-acciones',
    title: 'Acciones por renglón',
    body: 'Mirá el detalle o generá la factura o el remito de lo que quedó pendiente.',
    placement: 'left',
    pad: 6,
  },
  {
    targetId: 'informe-hilo',
    title: 'Hilo documental',
    body: 'Acá aparece la cadena de comprobantes (cotización, pedido, factura, remito…) de la fila seleccionada.',
    placement: 'top',
    pad: 6,
  },
];

/** `/ventas/informes/cuentas-cobrar` */
const CUENTAS_COBRAR_STEPS: PageTourStep[] = [
  {
    targetId: 'informe-acciones',
    title: 'Listados y exportación',
    body: 'Abrí listados especiales como la deuda vencida a fecha, o exportá el informe a Excel o PDF.',
    placement: 'bottom',
    pad: 6,
  },
  {
    targetId: 'informe-kpis',
    title: 'Indicadores',
    body: 'Deuda total, promedio de días de cobro del mes, clientes con deuda y cobros del mes actual.',
    placement: 'bottom',
    pad: 4,
  },
  {
    targetId: 'informe-filtros',
    title: 'Filtros',
    body: 'Buscá por número o nombre de cliente, filtrá por vendedor y elegí el orden. La lista se actualiza sola.',
    placement: 'bottom',
    pad: 6,
  },
  {
    targetId: 'informe-resultados',
    title: 'Saldos por cliente',
    body: 'Saldo total, vigente y vencido de cada cliente. Tocá una fila para abrir su resumen de cuenta.',
    placement: 'top',
    pad: 4,
  },
  {
    targetId: 'informe-fila-acciones',
    title: 'Acciones por cliente',
    body: 'Registrá un cobro (nuevo recibo) o una devolución directamente desde la fila.',
    placement: 'left',
    pad: 6,
  },
];

/** `/ventas/informes/resumen-cuenta` */
const RESUMEN_CUENTA_STEPS: PageTourStep[] = [
  {
    targetId: 'informe-cliente',
    title: 'Cliente',
    body: 'Buscá el cliente por número, nombre o CUIT. Al elegirlo ves su saldo total y vencido.',
    placement: 'bottom',
    pad: 4,
  },
  {
    targetId: 'informe-tabs',
    title: 'Composición y movimientos',
    body: '«Composición de saldo» muestra los comprobantes con saldo pendiente; «Movimientos históricos», todo el historial de la cuenta.',
    placement: 'bottom',
    pad: 4,
  },
  {
    targetId: 'informe-acciones',
    title: 'Filtros y acciones',
    body: 'Filtrá por fechas o texto, registrá una devolución, aplicá saldos pendientes y exportá a Excel o PDF.',
    placement: 'bottom',
    pad: 4,
  },
  {
    targetId: 'informe-resultados',
    title: 'Comprobantes',
    body: 'Detalle de comprobantes con debe, haber y saldo acumulado. Desde cada fila podés aplicar saldos o imprimir.',
    placement: 'top',
    pad: 4,
  },
];

/** `/ventas/informes/libro-iva-ventas` */
const LIBRO_IVA_VENTAS_STEPS: PageTourStep[] = [
  {
    targetId: 'informe-acciones',
    title: 'Exportación',
    body: 'Generá el IVA Simple, exportá a Excel, bajá los TXT para ARCA (comprobantes y alícuotas) o imprimí el libro en PDF.',
    placement: 'bottom',
    pad: 6,
  },
  {
    targetId: 'informe-filtros',
    title: 'Filtros',
    body: 'Elegí compañía, período, cliente y sucursal. El libro se actualiza al cambiar los filtros.',
    placement: 'bottom',
    pad: 4,
  },
  {
    targetId: 'informe-resultados',
    title: 'Libro IVA',
    body: 'Cada comprobante con neto gravado, IVA por alícuota, exento y total.',
    placement: 'top',
    pad: 4,
  },
  {
    targetId: 'informe-totales',
    title: 'Totales',
    body: 'Totales del período para cada columna del libro.',
    placement: 'top',
    pad: 4,
  },
];

/** `/ventas/informes/ventas-dinamico` */
const VENTAS_DINAMICO_STEPS: PageTourStep[] = [
  {
    targetId: 'informe-acciones',
    title: 'Cargar y exportar',
    body: 'Con «Cargar datos» traés las ventas según los filtros. Después podés exportar el dataset completo a Excel.',
    placement: 'bottom',
    pad: 6,
  },
  periodo(),
  {
    targetId: 'informe-filtros',
    title: 'Filtros avanzados',
    body: 'Acotá por fechas, cliente, artículo, tipo de formulario, sucursal y cantidad máxima de filas.',
    placement: 'bottom',
    pad: 4,
  },
  {
    targetId: 'informe-resultados',
    title: 'Tabla dinámica',
    body: 'Arrastrá campos a filas y columnas, elegí la agregación (suma, cantidad…) y el tipo de visualización para armar tu propio análisis.',
    placement: 'top',
    pad: 4,
  },
];

/** `/ventas/informes/ventas-por-producto` */
const VENTAS_POR_PRODUCTO_STEPS: PageTourStep[] = [
  {
    targetId: 'informe-generar',
    title: 'Generar informe',
    body: 'Ejecutá el informe con el período y los filtros elegidos.',
    placement: 'bottom',
    pad: 6,
  },
  periodo(),
  {
    targetId: 'informe-filtros',
    title: 'Filtros avanzados',
    body: 'Filtrá por sucursal, artículo, tipo, ramo, línea, clase, tipo de comprobante y categorización.',
    placement: 'bottom',
    pad: 4,
  },
  {
    targetId: 'informe-acciones',
    title: 'Exportar',
    body: 'Exportá los resultados a CSV o Excel, o imprimí el informe.',
    placement: 'bottom',
    pad: 6,
  },
  {
    targetId: 'informe-kpis',
    title: 'Indicadores',
    body: 'Monto neto, cantidades, ticket promedio, costo, utilidad, rentabilidad, peso y volumen del período.',
    placement: 'bottom',
    pad: 4,
  },
  {
    targetId: 'informe-resultados',
    title: 'Resumen por clasificación',
    body: 'Solapas por ramo, línea, clase y producto con gráfico y tabla. Tocá una fila para bajar al detalle de productos.',
    placement: 'top',
    pad: 4,
  },
];

/** `/ventas/informes/ventas-resultados` */
const VENTAS_RESULTADOS_STEPS: PageTourStep[] = [
  {
    targetId: 'informe-generar',
    title: 'Generar informe',
    body: 'Ejecutá el informe con el período y los filtros elegidos.',
    placement: 'bottom',
    pad: 6,
  },
  periodo(),
  {
    targetId: 'informe-filtros',
    title: 'Filtros',
    body: 'Elegí fechas, sucursal, ubicación y, si querés, un cliente puntual.',
    placement: 'bottom',
    pad: 4,
  },
  {
    targetId: 'informe-acciones',
    title: 'Exportar',
    body: 'Exportá los resultados a CSV, Excel o PDF. Al lado ves la cantidad de comprobantes y el total.',
    placement: 'bottom',
    pad: 6,
  },
  {
    targetId: 'informe-resultados',
    title: 'Comprobantes',
    body: 'Comprobantes del período con cliente, sucursal, vendedor, forma de pago y total.',
    placement: 'top',
    pad: 4,
  },
  {
    targetId: 'informe-fila-acciones',
    title: 'Ver artículos',
    body: 'Desplegá un comprobante para ver sus artículos con cantidad, precio y subtotal.',
    placement: 'right',
    pad: 6,
  },
];

/** `/ventas/informes/personalizados` */
const INFORMES_PERSONALIZADOS_STEPS: PageTourStep[] = [
  {
    targetId: 'informe-store',
    title: 'Store de informes',
    body: 'Abrí el store para instalar informes de la biblioteca y volvé a «Mis informes» para usarlos.',
    placement: 'bottom',
    pad: 6,
  },
  {
    targetId: 'informe-filtros',
    title: 'Búsqueda y vista',
    body: 'Buscá informes por nombre o descripción y elegí verlos como detalle, tarjetas o íconos.',
    placement: 'bottom',
    pad: 6,
  },
  {
    targetId: 'informe-resultados',
    title: 'Mis informes',
    body: 'Informes instalados para la empresa, con su versión y cantidad de parámetros.',
    placement: 'top',
    pad: 4,
  },
  {
    targetId: 'informe-fila-acciones',
    title: 'Ejecutar',
    body: 'Ejecutá el informe: si tiene parámetros te los pide y después muestra el PDF.',
    placement: 'bottom',
    pad: 6,
  },
];

export const SEGUIMIENTO_VENTAS_TOUR: PageTourDefinition = { id: 'informe-seguimiento-ventas', steps: SEGUIMIENTO_VENTAS_STEPS };
export const CUENTAS_COBRAR_TOUR: PageTourDefinition = { id: 'informe-cuentas-cobrar', steps: CUENTAS_COBRAR_STEPS };
export const RESUMEN_CUENTA_TOUR: PageTourDefinition = { id: 'informe-resumen-cuenta', steps: RESUMEN_CUENTA_STEPS };
export const LIBRO_IVA_VENTAS_TOUR: PageTourDefinition = { id: 'informe-libro-iva-ventas', steps: LIBRO_IVA_VENTAS_STEPS };
export const VENTAS_DINAMICO_TOUR: PageTourDefinition = { id: 'informe-ventas-dinamico', steps: VENTAS_DINAMICO_STEPS };
export const VENTAS_POR_PRODUCTO_TOUR: PageTourDefinition = { id: 'informe-ventas-por-producto', steps: VENTAS_POR_PRODUCTO_STEPS };
export const VENTAS_RESULTADOS_TOUR: PageTourDefinition = { id: 'informe-ventas-resultados', steps: VENTAS_RESULTADOS_STEPS };
export const INFORMES_PERSONALIZADOS_TOUR: PageTourDefinition = { id: 'informes-personalizados', steps: INFORMES_PERSONALIZADOS_STEPS };
